/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Table, Button, Modal, ModalHeader,
  ModalBody, ModalFooter, Form, FormGroup, Label, Input, Badge,
} from 'reactstrap';
import styled, { keyframes } from 'styled-components';
import bankAccountService, { BankAccount } from '../../services/bankAccount.service';
import { useAppSelector } from '../../hooks';

/* ── Animations ─────────────────────────────────────────────── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); }`;

const PageWrapper = styled.div`
  animation: ${fadeIn} 0.4s ease-out;
  padding: 24px;
`;

const PageTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
`;

const StyledTable = styled(Table)`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  thead th {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    font-weight: 700;
    padding: 14px 16px;
  }
  tbody tr {
    transition: background 0.15s;
    &:hover { background: #f0f4ff; }
  }
  td { vertical-align: middle; padding: 12px 16px; }
`;

const AddButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 24px;
  padding: 8px 22px;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(102,126,234,0.35);
  &:hover { opacity: 0.88; }
`;

const ActionBtn = styled(Button)`
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0 3px;
`;

/* ── Component ───────────────────────────────────────────────── */
const emptyForm = { bankName: '', accountNumber: '', accountHolder: '' };

const BankAccountManage: React.FC = () => {
  const navigate = useNavigate();
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);
  const isAdmin = userInfo?.role === 0;
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<BankAccount | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await bankAccountService.getAll();
      setAccounts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const openAdd = () => {
    setEditTarget(null);
    setFormData(emptyForm);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (acc: BankAccount) => {
    setEditTarget(acc);
    setFormData({ bankName: acc.bankName, accountNumber: acc.accountNumber, accountHolder: acc.accountHolder });
    setError('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    if (!formData.bankName.trim() || !formData.accountNumber.trim() || !formData.accountHolder.trim()) {
      setError('Vui lòng điền đầy đủ tất cả các trường.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editTarget) {
        await bankAccountService.update(editTarget.id, formData);
      } else {
        await bankAccountService.create(formData);
      }
      setModalOpen(false);
      await load();
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (acc: BankAccount) => {
    if (!isAdmin) return;
    if (!window.confirm(`Xoá tài khoản "${acc.accountNumber} - ${acc.bankName}"?`)) return;
    try {
      await bankAccountService.delete(acc.id);
      await load();
    } catch (err: any) {
      alert(err.message || 'Có lỗi khi xoá');
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ── Admin guard ─────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', gap: 16, textAlign: 'center',
      }}>
        <div style={{ fontSize: 64 }}>🔒</div>
        <h3 style={{ fontWeight: 800, color: '#dc2626' }}>Không có quyền truy cập</h3>
        <p style={{ color: '#64748b', maxWidth: 360 }}>
          Trang này chỉ dành cho Admin. Bạn không có quyền xem nội dung này.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'linear-gradient(135deg,#667eea,#764ba2)',
            color: '#fff', border: 'none', borderRadius: 24,
            padding: '10px 28px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
          }}
        >
          ← Quay lại
        </button>
      </div>
    );
  }

  return (
    <PageWrapper>
      <Container fluid>
        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col>
            <PageTitle>🏦 Quản lý Tài khoản Ngân hàng</PageTitle>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 0 }}>
              Quản lý danh sách tài khoản ngân hàng dùng để nhận học phí
            </p>
          </Col>
          <Col xs="auto">
            <AddButton onClick={openAdd}>+ Thêm tài khoản</AddButton>
          </Col>
        </Row>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', fontSize: '1rem' }}>
            ⏳ Đang tải...
          </div>
        ) : accounts.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', color: '#94a3b8',
            background: '#f8fafc', borderRadius: 16, fontSize: '1rem',
          }}>
            Chưa có tài khoản ngân hàng nào. Hãy thêm tài khoản đầu tiên!
          </div>
        ) : (
          <StyledTable responsive hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Tên ngân hàng</th>
                <th>Số tài khoản</th>
                <th>Chủ tài khoản</th>
                <th style={{ textAlign: 'center' }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((acc, idx) => (
                <tr key={acc.id}>
                  <td>
                    <Badge color="light" style={{ color: '#667eea', fontWeight: 700, fontSize: '0.85rem' }}>
                      {idx + 1}
                    </Badge>
                  </td>
                  <td style={{ fontWeight: 600 }}>{acc.bankName}</td>
                  <td>
                    <span style={{
                      fontFamily: 'monospace', fontSize: '1rem',
                      background: '#f0f4ff', padding: '2px 10px', borderRadius: 8,
                      color: '#4338ca', fontWeight: 700, letterSpacing: 1,
                    }}>
                      {acc.accountNumber}
                    </span>
                  </td>
                  <td style={{ textTransform: 'uppercase', fontWeight: 600, color: '#374151' }}>
                    {acc.accountHolder}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <ActionBtn color="primary" outline onClick={() => openEdit(acc)}>✏️ Sửa</ActionBtn>
                    <ActionBtn color="danger" outline onClick={() => handleDelete(acc)}>🗑️ Xoá</ActionBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        )}

        {/* Add/Edit Modal */}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
          <ModalHeader
            toggle={() => setModalOpen(false)}
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}
          >
            {editTarget ? '✏️ Sửa tài khoản ngân hàng' : '➕ Thêm tài khoản ngân hàng'}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label>Tên ngân hàng <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  name="bankName"
                  placeholder="VD: MB Bank, TPBank, Vietcombank..."
                  value={formData.bankName}
                  onChange={handleInput}
                />
              </FormGroup>
              <FormGroup>
                <Label>Số tài khoản <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  name="accountNumber"
                  placeholder="VD: 0941858482"
                  value={formData.accountNumber}
                  onChange={handleInput}
                />
              </FormGroup>
              <FormGroup>
                <Label>Tên chủ tài khoản <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  name="accountHolder"
                  placeholder="VD: LE THI TAM (viết hoa, không dấu)"
                  value={formData.accountHolder}
                  onChange={handleInput}
                />
              </FormGroup>
              {error && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 8, padding: '10px 14px', color: '#dc2626',
                  fontSize: '0.87rem', marginTop: 8,
                }}>
                  ⚠️ {error}
                </div>
              )}
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Huỷ</Button>
            <Button
              style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', border: 'none', fontWeight: 700 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '⏳ Đang lưu...' : editTarget ? '💾 Cập nhật' : '➕ Thêm'}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </PageWrapper>
  );
};

export default BankAccountManage;
